
import {
  defineComponent, onBeforeUnmount, onMounted, PropType, ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import AdyenCheckout from '@adyen/adyen-web';
import { adyenConfig } from '@/composables/user/AdyenConfig';
import { UserState } from '@/composables/User';
import { ActionResult, fetchDropinConfig } from '@/composables/billing/billingApi';

export default defineComponent({
  name: 'AdyenCardDropin',

  props: {
    amount: {
      type: Number as PropType<number>,
      required: true,
    },

    currency: {
      type: String as PropType<'EUR' | 'USD'>,
      default: 'EUR',
    },

    submitHandler: {
      type: Function as PropType<(stateData: Record<string, any>) => Promise<ActionResult>>,
      required: true,
    },

    detailsHandler: {
      type: Function as PropType<(stateData: Record<string, any>) => Promise<ActionResult>>,
      required: true,
    },

    /**
     * Whitelist of Adyen payment method types the Drop-in is allowed to show.
     *   - `scheme`            → cartes bancaires (Visa, MC, Amex...)
     *   - `paypal`            → PayPal
     *   - `sepadirectdebit`   → prélèvement SEPA (« virement bancaire » FR)
     *
     * Adyen only renders methods that BOTH appear in this list AND are
     * enabled on the merchant account. Default = cards only (back-compat
     * with the previous hardcoded behaviour).
     */
    allowedMethods: {
      type: Array as PropType<string[]>,
      default: () => ['scheme'],
    },
  },

  emits: ['success', 'error'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const containerId = `adyen-dropin-${Math.random().toString(36).slice(2, 9)}`;
    const loading = ref(true);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let dropin: any = null;

    const userLang: string = UserState.user.lang ?? 'fr';
    const countryCode: string = userLang.toLocaleUpperCase();
    const adyenLocale = `${userLang}_${countryCode}`;
    const adyenEnvironment: string = process.env.VUE_APP_ADYEN_ENVIRONMENT ?? 'live';

    const successMessage = () => t('billing.review.paymentAccepted');

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const routeResult = async (result: ActionResult, d: any) => {
      switch (result.status) {
        case 'completed':
          d.setStatus('success', { message: successMessage() });
          emit('success');
          break;
        case 'action_required':
          d.handleAction(result.action);
          break;
        case 'failed':
          // The parent unmounts the Drop-in on error and shows its own card
          // with a retry CTA — calling setStatus('error') would render
          // Adyen's giant red X on top of ours.
          emit('error', result.error);
          break;
        default:
          emit('error', t('billing.payment.genericError'));
          break;
      }
    };

    const initDropin = async () => {
      try {
        const paymentMethods = await fetchDropinConfig({
          amount: props.amount,
          currency: props.currency,
          countryCode,
          locale: adyenLocale,
        });

        const configuration = {
          paymentMethodsResponse: paymentMethods,
          // Whitelist driven by the parent component (CB only / + PayPal /
          // + SEPA according to the surrounding flow). No `removePaymentMethods`
          // — the whitelist alone decides what shows up.
          allowPaymentMethods: props.allowedMethods,
          clientKey: adyenConfig[adyenEnvironment]['client-key'],
          locale: adyenLocale,
          environment: adyenConfig[adyenEnvironment].environment,

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onSubmit: async (state: any, d: any) => {
            // eslint-disable-next-line no-console
            console.log('[ADYEN] onSubmit fired', { stateData: state?.data });
            d.setStatus('loading');
            try {
              const result = await props.submitHandler(state.data);
              // eslint-disable-next-line no-console
              console.log('[ADYEN] onSubmit → submitHandler result', result);
              await routeResult(result, d);
            } catch (e: unknown) {
              // eslint-disable-next-line no-console
              console.error('[ADYEN] onSubmit caught error', e);
              const msg = e instanceof Error ? e.message : t('billing.payment.genericError');
              d.setStatus('error', { message: msg });
              emit('error', msg);
            }
          },

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onAdditionalDetails: async (state: any, d: any) => {
            // eslint-disable-next-line no-console
            console.log('[ADYEN] onAdditionalDetails fired', { stateData: state?.data });
            try {
              const result = await props.detailsHandler(state.data);
              // eslint-disable-next-line no-console
              console.log('[ADYEN] onAdditionalDetails → detailsHandler result', result);
              await routeResult(result, d);
            } catch (e: unknown) {
              // eslint-disable-next-line no-console
              console.error('[ADYEN] onAdditionalDetails caught error', e);
              const msg = e instanceof Error ? e.message : t('billing.payment.genericError');
              d.setStatus('error', { message: msg });
              emit('error', msg);
            }
          },

          // Zero-auth flows (amount = 0, e.g. add-card / extend-trial) end
          // with the SDK firing `onPaymentCompleted` INSTEAD of
          // `onAdditionalDetails`. Reason : the ACS (Marqeta) returns the
          // final `Authorised` directly after the OOB challenge so Adyen
          // skips the /payments/details round-trip. If this hook is not
          // declared, the Drop-in container is left blank after the
          // challenge iframe self-unmounts → infamous « modale blanche
          // après 3DS ». We finalise the flow exactly like a successful
          // /payments/details would have : success status + emit('success')
          // so the parent dialog refetches the overview (the actual card
          // tokenisation is performed server-side via the Adyen webhook).
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onPaymentCompleted: (result: any, d: any) => {
            // eslint-disable-next-line no-console
            console.log('[ADYEN] onPaymentCompleted fired', { result });
            try {
              const code = result?.resultCode;
              if (code === 'Authorised' || code === 'Received') {
                d.setStatus('success', { message: successMessage() });
                emit('success');
                return;
              }
              // Anything else (Refused, Cancelled, Error) = surface a failure.
              const msg =
                result?.refusalReason
                ?? result?.message
                ?? t('billing.payment.genericError');
              emit('error', msg);
            } catch (e: unknown) {
              const msg = e instanceof Error ? e.message : t('billing.payment.genericError');
              emit('error', msg);
            }
          },

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onError: (error: any) => {
            // eslint-disable-next-line no-console
            console.error('[ADYEN] onError fired', {
              name: error?.name,
              message: error?.message,
              error,
            });
          },

          paymentMethodsConfiguration: {
            card: {
              hasHolderName: true,
              holderNameRequired: true,
              hideCVC: false,
            },

            threeDS2: { challengeWindowSize: '05' },
          },
        };

        const checkout = await AdyenCheckout(configuration);
        dropin = checkout
          .create('dropin', { showStoredPaymentMethods: false })
          .mount(`#${containerId}`);
        // eslint-disable-next-line no-console
        console.log('[ADYEN] Drop-in mounted', { containerId });
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      // eslint-disable-next-line no-console
      console.log('[ADYEN] component mounted — initDropin starting', { containerId });
      initDropin();
    });

    onBeforeUnmount(() => {
      // eslint-disable-next-line no-console
      console.log('[ADYEN] component onBeforeUnmount — unmounting dropin', {
        containerId,
        hadDropin: !!dropin,
      });
      if (dropin) {
        try {
          dropin.unmount();
        } catch {
          /* noop */
        }
      }
    });

    return {
      t,
      containerId,
      loading,
    };
  },
});
